import {
  inject,
  Injectable,
  Injector,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { selectSupportLinks } from '@app/_store/features/support/support.selectors';
import {
  getNavigationConfig,
  NavigationStateKeys,
} from '@core/configs/navigation.config';
import { Navigation } from '@core/models/interfaces/navigation';
import { SupportLinks } from '@core/models/interfaces/storage-data/support-settings';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import {
  FuseNavigationItem,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { Confirmations } from '@fuse/services/confirmation/confirmations.config';
import { Store } from '@ngrx/store';
import {
  filter,
  firstValueFrom,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private store = inject(Store);
  private confirmation = inject(FuseConfirmationService);
  private injector = inject(Injector);
  private dialog = inject(MatDialog);
  private mixpanel = inject(MixpanelService);
  private _fuseNavigationService = inject(FuseNavigationService);

  public supportLinks!: SupportLinks;
  private navigationStateKey: WritableSignal<NavigationStateKeys> =
    signal('app');
  private _navigationStates?: Partial<
    Record<NavigationStateKeys, FuseNavigationItem[]>
  >;
  private _navigation: ReplaySubject<Navigation> =
    new ReplaySubject<Navigation>(1);

  constructor() {
    this.store
      .select(selectSupportLinks)
      .pipe(filter(Boolean), take(1))
      .subscribe((links: SupportLinks) => {
        this.supportLinks = links;
        this._navigationStates = getNavigationConfig(this, this.mixpanel);
        this.setNavigation(this.navigationStateKey());
      });
  }

  public get appStateKey(): NavigationStateKeys {
    return this.navigationStateKey();
  }

  /**
   * Observable stream of navigation data.
   * @returns Observable containing navigation structure.
   */
  public get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  public setNavigation(stateKey: NavigationStateKeys) {
    this.navigationStateKey.set(stateKey);

    const state = this._navigationStates?.[stateKey];
    if (state) this._navigation.next({ state });
  }

  public navItemClick(item: FuseNavigationItem | string) {
    const action = typeof item === 'string' ? item : item.id;
    this.toggleNavigation('mainNavigation');

    switch (action) {
      case 'logout': {
        this.onLogoutClick();
        break;
      }
      case 'delete': {
        this.onDeleteClick();
        break;
      }
      default: {
        break;
      }
    }
  }

  private onLogoutClick() {
    const dialogRef = this.confirmation.open(Confirmations.logout);
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        switchMap(async (result) => {
          if (result === 'confirmed') {
            // S4 (same as A8 below): the auth provider is a feature service;
            // resolve it at the click so the core layer holds no static edge.
            const { AuthDataProviderService } =
              await import('@app/features/auth/_common/services/auth-data-provider.service');
            return firstValueFrom(
              this.injector.get(AuthDataProviderService).logout()
            );
          }
          return void 0;
        })
      )
      .subscribe((): undefined => undefined);
  }

  private async onDeleteClick() {
    const { AuthDataProviderService } =
      await import('@app/features/auth/_common/services/auth-data-provider.service');
    const authDataProvider = this.injector.get(AuthDataProviderService);
    const from = firstLetterUppercase(this.navigationStateKey() || '');
    // A8. The modal lives with the support feature now (it sends support
    // feedback); the dynamic import keeps this eager service from dragging the
    // support chunk into the initial bundle.
    const { NeedHelpModalComponent } =
      await import('@app/features/support/_modals/need-help-modal/need-help-modal.component');
    this.dialog
      .open(NeedHelpModalComponent, {
        panelClass: 'default-modal',
        autoFocus: false,
        data: {
          from,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        switchMap((result) => {
          if (result === 'skip') {
            return this.confirmation.open(Confirmations.delete).afterClosed();
          } else if (result === 'confirmed') {
            this.confirmation.open(Confirmations.supportResponse);
          }
          return of(void 0);
        }),
        switchMap((reason) => {
          if (reason === 'confirmed') {
            return authDataProvider.leavePocketGuard().pipe(
              tap((response) => {
                if (response?.errorReason && response?.errorSource) {
                  this.mixpanel.track('Close account - Denied', {
                    From: from,
                    'Contact support': false,
                    'Error source': response.errorSource,
                    'Error reason': response.errorReason,
                  });
                } else {
                  this.mixpanel.track('Close account - Complete', {
                    From: from,
                    'Contact support': false,
                  });
                }
              })
            );
          }

          this.mixpanel.track('Close account - Exit', {
            From: from,
            'Contact support': false,
          });

          return of(void 0);
        })
      )
      .subscribe();
  }

  public getContactSupportEntryPoint() {
    switch (this.appStateKey) {
      case 'quiz':
        return 'Quiz';
      // There used to be a `case 'paywall'` returning 'Paid app - final
      // paywall' here, for a state no route ever sets. The blocked-paywall
      // route passes 'blocked', which has always fallen through to 'Side menu'
      // — if that label was meant for it, that is a deliberate analytics
      // decision to make, not something to infer from a dead case.
      default:
        return 'Side menu';
    }
  }

  private toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}

/** Same semantics as FirstLetterUppercasePipe — the rest of the string keeps its case. */
function firstLetterUppercase(value: string | null | undefined): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}
